import { get as safeGet, isEmpty } from 'lodash';
import { formatInTimeZone, format } from 'date-fns-tz';
import {
	isWithinInterval,
	format as dateFnsFormat,
	parseISO,
	isValid,
	add,
	startOfDay,
	endOfDay,
	getYear,
	isAfter,
} from 'date-fns';

import { FY_END_DATE } from 'core/constants';

export const DEFAULT_TIMEZONE = 'Australia/Sydney';
export const AU_EN_LOCALE = 'en-AU';

export enum DATE_FORMATS {
	DEFAULT = 'dd/MM/yyyy',
	CONDENSED = 'dd/MM/yy',
	/** E.g. 25 June 2021 */
	DAY_MONTH_NAME_YEAR = 'dd MMMM yyyy',
	/** E.g. 25 Jun 2021 */
	DATE_SHORT_MONTH = 'dd MMM yyyy',
	/** E.g 1 June 2021, 1:00PM */
	DATE_WITH_TIME = 'd MMMM yyyy, h:mmaa',
	/** E.g 1:00PM */
	TIME_ONLY = 'h:mmaa',
	/** E.g 7 June 2021 */
	/** Only used if using date-fns - full month name */
	DAY_MONTH_NAME_YEAR_FNS = 'd MMMM yyyy',
	/** E.g 7 Jun 2021 */
	/** Only used if using date-fns - short month name */
	DAY_SHORT_MONTH_NAME_YEAR_FNS = 'd MMM yyyy',
	/**2025-01-16 */
	YEAR_SHORT_MONTH_DAY = 'yyyy-MM-dd',
}
const UTC_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;
const INVALID_DATE = 'Invalid Date';

/**
 * Check if a date string is in UTC format
 *
 * @param {string} date - Date string to check.
 * @returns {boolean} - True if the date string is in UTC format, false otherwise.
 */
export const isUtcDate = (date: string): boolean => {
	return UTC_DATE_PATTERN.test(date);
};

/**
 * Format date from the data
 *
 * @param {date | Date} date - string.
 * @param {format} format    - Type of format string.
 */
export const convertDate = (date: string | Date, format: string) => {
	let result = INVALID_DATE;
	const parsedDate = typeof date === 'string' ? parseISO(date) : date;
	const isUtc = typeof date === 'string' && isUtcDate(date);

	if (date && isValid(parsedDate)) {
		result = isUtc
			? dateFnsFormat(parsedDate, format)
			: dateFnsFormat(new Date(date), format);
	}

	return result;
};

/**
 * Format non UTC date
 *
 * @param {date | Date} date - string.
 * @param {format} dateFormat    - Type of format string.
 */
//TO DO: check if we can rename this function since it can accept utc and non-utc dates
export const convertNonUtcDate = (date: string | Date, dateFormat: string) => {
	return formatInTimeZone(new Date(date), 'Australia/Sydney', dateFormat);
};

/**
 * @description Convert timezone, and format date as per business rules
 * @param {string | Date} date - Date to be converted
 * @param {string} dateFormat - Format of date'.
 * @param {string} timeFormat - Format of time.
 * @param {enums} displayType - Handles two different display.
 * @param {string} timeZone - timeZone value, if timezone is not needed - default value to pass is N/A
 */
export const convertTimezoneWithCustomDateFormat = (
	date: string | Date,
	dateFormat: string,
	timeFormat: string,
	displayType: 'full-with-comma' | 'partial-no-comma',
	timeZone?: string,
) => {
	const parsedDate = typeof date === 'string' ? parseISO(date) : date;

	if (
		!isValid(parsedDate) ||
		typeof date !== 'string' ||
		typeof dateFormat !== 'string' ||
		typeof timeFormat !== 'string' ||
		typeof displayType !== 'string'
	) {
		return INVALID_DATE;
	}

	const defaultTimeZone = 'Australia/Sydney';
	const formattedDate = formatInTimeZone(
		new Date(date),
		defaultTimeZone,
		dateFormat,
	);
	const formattedTimeWithTZ = formatInTimeZone(
		new Date(date),
		defaultTimeZone,
		timeFormat,
	).toLowerCase();
	const formattedTime =
		timeZone === 'N/A'
			? format(new Date(date), timeFormat).toLowerCase()
			: formattedTimeWithTZ;

	return displayType === 'partial-no-comma'
		? `${formattedDate} ${formattedTime}`
		: `${formattedDate}, ${formattedTime}`;
};

export function formatDate(
	timeStamp: string,
	dateFormat: string = DATE_FORMATS.DEFAULT,
) {
	const date = new Date(timeStamp);

	// Since we only support two date formats at the moment, check if it's the condensed variant, otherwise return the
	// default no matter what it is. The only supported formats are those in the enums
	return dateFormat.toLowerCase().replace('mm', 'MM') === DATE_FORMATS.CONDENSED
		? dateFnsFormat(date, DATE_FORMATS.CONDENSED)
		: dateFnsFormat(date, DATE_FORMATS.DEFAULT);
}

export function getDateFormatFromSitecoreContext(
	sitecoreSiteSettings: any,
): string {
	const dateFormatSettingsObject = sitecoreSiteSettings.find(
		(setting: { dateFormat: string }) => setting.dateFormat,
	);

	return safeGet(
		dateFormatSettingsObject,
		'dateFormat[0].key',
		DATE_FORMATS.DEFAULT,
	);
}

/**
 * Get the closest Financial Year date.
 *
 * @param {Date} date                           - the given date
 * @param {boolean=true} shouldGetPrecedingFY - should whether calculate the closest year by look back to the preceding year
 * @return {string} the closest FY end date string
 * @example
 * ("2019-07-01", true) => "2019-06-30"
 * ("2019-07-01", false) => "2020-06-30"
 */
const getClosestFYEndDate = (date: Date, shouldGetPrecedingFY = true) => {
	const parsedDate = new Date(date); // Parse the input date
	const currentYear = getYear(parsedDate); // Get the year from the date
	const fyEnd = endOfDay(parseISO(`${currentYear}-${FY_END_DATE}`)); // Get the end of the FY end date
	// Check if the given date is after the FY end date
	const isAfterFYEnd = isAfter(parsedDate, fyEnd);
	let revisedYear = currentYear;

	// Decrement the year to get preceding year if the given date is before the FY end date of its current year
	if (shouldGetPrecedingFY && !isAfterFYEnd) revisedYear -= 1;
	// Increment the year to get next year if the given date is After the FY end date of its current year
	if (!shouldGetPrecedingFY && isAfterFYEnd) revisedYear += 1;

	return { year: revisedYear, fyEndDate: `${revisedYear}-${FY_END_DATE}` };
};

interface IGetFYEndDates {
	endDate: Date;
	numberOfYears?: number;
	startDate?: Date;
}

/**
 * Get a list of Financial Year dates.
 *
 * @param {Date} param0.endDate          - the end date of the period of FYs
 * @param {number=} param0.numberOfYears - if providing, get the given number of FY from the endDate
 * @param {Date=} param0.endDate         - if providing, get FY between startDate and the endDate
 * @returns {string[]} array of FY end dates
 */
export const getFYEndDates = ({
	endDate,
	numberOfYears,
	startDate,
}: IGetFYEndDates): string[] => {
	const { year: endFYYear, fyEndDate: endFYDate } =
		getClosestFYEndDate(endDate);
	const shouldGenerateDatesByInterval = typeof numberOfYears === 'number';

	// Priorities to use numberOfYears to get FY end dates
	if (shouldGenerateDatesByInterval) {
		return Array.from(
			Array(numberOfYears),
			(_, i) => `${endFYYear - i}-${FY_END_DATE}`,
		);
	}

	// If starDate present, use it to get FY end dates
	if (startDate) {
		if (startDate > endDate) {
			throw new Error(
				'Please check the startDate to ensure it is before the endDate',
			);
		}

		const { year: startFYYear } = getClosestFYEndDate(startDate, false);

		if (startFYYear < endFYYear) {
			// Returns all FY end dates between the given startFYYear and endFYYear
			return Array.from(
				Array(endFYYear - startFYYear + 1),
				(_, i) => `${endFYYear - i}-${FY_END_DATE}`,
			);
		} else if (startFYYear > endFYYear) {
			// No valid FY years between the given period
			return [];
		}
	}

	return [endFYDate];
};

/**
 * Check if the a date is within a duration.
 *
 * @param date	 - the date that is being checked
 * @param target - the end date of the duration
 * @param amount - amount of the duration unit
 * @param unit 	 - unit of duration, e.g. 'day', 'month'
 */
export const checkWithinDuration = (
	date: Date,
	target: Date,
	amount: number,
	unit: 'day' | 'month' | 'year' = 'month',
) => {
	// Calculate the start and end of the duration
	const endOfDuration = endOfDay(target);
	const startOfDuration = startOfDay(
		unit === 'day'
			? add(target, { days: -(amount ?? 0) })
			: unit === 'month'
			? add(target, { months: -(amount ?? 0) })
			: add(target, { years: -(amount ?? 0) }),
	);

	// Check if the date is within the interval
	return isWithinInterval(date, { start: startOfDuration, end: endOfDuration });
};

/**
 * Get Current Financial Dates
 * @returns { from: string; to: string; }
 */
export const getCurrentFinancialDates = () => {
	const today = new Date();

	return today.getMonth() + 1 < 7
		? {
				from: `${today.getFullYear() - 1}-07-01`,
				to: `${today.getFullYear()}-06-30`,
		  }
		: {
				from: `${today.getFullYear()}-07-01`,
				to: `${today.getFullYear() + 1}-06-30`,
		  };
};

/**
 * Check if the server date time is within the publish and unpublish date period.
 *
 * @param date	 - server date time from sitecore context.
 * @param publishDate - start date on when to show specific component
 * @param unpublishDate - end date on when to show specific component
 */
export const checkWithinPublishPeriod = (
	date: Date,
	publishDate: Date,
	unpublishDate: Date,
) => {
	const result = isWithinInterval(new Date(date), {
		start: publishDate,
		end: unpublishDate,
	});

	return result;
};

/**
 * @description - Dedicated for withdrawals components. Converts an input string date into 'YYYY-MM-DD' format
 * @param stringDate - "August 28, 1960"
 *
 * @example withdrawalsDateparser(September 18, 1964)
 * @returns {string} "1964-09-18"
 */
export const withdrawalsDateparser = (stringDate: string) => {
	const date = new Date(stringDate);
	const day = date.getDate() > 9 ? date.getDate() : `0${date.getDate()}`;
	let month: string | number = date.getMonth() + 1;
	month = month > 9 ? month : `0${month}`;
	const year = date.getFullYear();
	// format your date as you expect
	const dateFormat = `${year}-${month}-${day}`;

	return dateFormat;
};

/**
 * Check if the current date time is within the start and end date of restriction period.
 *
 * @param restrictionStartDate - start date of the restriction period
 * @param restrictionEndDate - end date of the restriction period
 */
export const checkWithinRestrictionPeriod = (
	restrictionStartDate: string,
	restrictionEndDate: string,
) => {
	let isRestricted = true;

	if (isEmpty(restrictionStartDate) || isEmpty(restrictionEndDate)) {
		return { isRestricted: false, formattedRestrictionEnd: '' };
	}

	const restrictionPeriod = {
		startTime: new Date(restrictionStartDate),
		endTime: new Date(restrictionEndDate),
	};

	const today = new Date();
	const nowInUsersTimezone = new Date(
		today.toLocaleString('en-US', { timeZone: DEFAULT_TIMEZONE }),
	);

	const restrictionStart = new Date(
		restrictionPeriod.startTime.toLocaleString('en-US', {
			timeZone: DEFAULT_TIMEZONE,
		}),
	);
	const restrictionEnd = new Date(
		restrictionPeriod.endTime.toLocaleString('en-US', {
			timeZone: DEFAULT_TIMEZONE,
		}),
	);

	const formattedRestrictionEnd = convertTimezoneWithCustomDateFormat(
		restrictionEndDate,
		'd MMM',
		'hh:mm a',
		'full-with-comma',
		'Australia/Sydney',
	);

	if (
		isWithinInterval(nowInUsersTimezone, {
			start: restrictionStart,
			end: restrictionEnd,
		})
	) {
		isRestricted = true;
	} else {
		isRestricted = false;
	}

	return { isRestricted, formattedRestrictionEnd };
};

/**
 *  format date ( e.g., 2024-12-19 ) to  (19 Dec 2024)
 */
export const formatFullDate = (dateString: string): string => {
	const date = new Date(dateString);

	const day = date.getDate();
	const month = date.toLocaleString('en-US', { month: 'short' }); // e.g., "Dec"
	const year = date.getFullYear();

	return `${day} ${month} ${year}`;
};

/***
 * Formats an ISO date string into a localized date string.
 * @param {string | undefined} isoDate - The ISO date string to be formatted.
 * @param {Intl.DateTimeFormatOptions} [options] - Optional formatting options.
 * @returns {string} The formatted date string or an empty string if the input is undefined or not in the ISO format.
 */
export const formatToLocaleDateString = (
	isoDate: string | undefined,
	options?: Intl.DateTimeFormatOptions,
): string => {
	// Check if isoDate is undefined or doesn't match the expected ISO date format
	if (!isoDate || !/^\d{4}-\d{2}-\d{2}/.test(isoDate)) {
		// Return empty string for undefined or non-ISO date format
		return '';
	}

	// Extract the date portion of isoDate and create a Date object
	const slicedDate = new Date(isoDate.substring(0, 10));

	// Options for formatting the date
	const formatOptions: Intl.DateTimeFormatOptions = options ?? {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
	};

	// Format the date using the specified options and return the result
	return slicedDate.toLocaleDateString(AU_EN_LOCALE, formatOptions);
};

/*****
 * Formats an ISO date string into a long, readable date and time format.
 * @param {string | undefined} isoDateString - The ISO date string to be formatted (Example: 2025-12-15T14:51:07).
 * @returns {string} The formatted date and time string or an empty string if the input is undefined or invalid.
 * The output format is: "h:mm AM/PM (AEDT/AEST) d MMMM yyyy"
 * Example: "2pm (AEDT/AEST) 15 December 2025"
 */
export const formatToLongReadableDateTime = (
	isoDateString: string | undefined,
): string => {
	const isoDate = new Date(isoDateString ?? '');
	if (!isValid(isoDate)) {
		return '';
	}

	const dateString = formatToLocaleDateString(isoDateString);
	const timeString = isoDate
		.toLocaleTimeString(AU_EN_LOCALE, {
			hour: 'numeric',
			hour12: true,
		})
		?.replace(':00', '') // Remove minutes if they are ":00"
		?.replace(' ', ''); // Remove space before the AM/PM

	return `${timeString} (AEDT/AEST) ${dateString}`;
};
